import React, { useEffect, useState } from "react";
import { showToast } from "../../../components/custom/custom-toast";
import AdjustmentRequestService, {
  CountingAdjustmentRequest,
  CountingDebtImpact,
  DebtShortageReason,
  DEBT_SHORTAGE_REASON_LABEL,
  ADJUSTMENT_REQUEST_STATUS,
  STAGE_LABEL,
} from "../services/adjustment-request-service";
import { FaultTicket, FAULT_STATUS, FAULT_STATUS_LABEL, FAULT_TYPE_LABEL, formatDateTime } from "../../fault-ticket/services/fault-ticket-service";
import ImageCustom from "../../../components/custom/image-custom";

interface Props {
  open: boolean;
  request: CountingAdjustmentRequest | null;
  onClose: () => void;
  onUpdated: () => void;
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="text-slate-800 font-medium text-right">{value}</span>
  </div>
);

/** Gộp số CŨ (source_items) + số ĐỀ XUẤT theo từng loại đồ, để so cạnh nhau — kể cả loại chỉ có ở 1 bên. */
function buildCompareRows(
  oldItems: { category: string; quantity: number }[],
  newItems: { category: string; quantity: number }[],
) {
  const oldMap = new Map(oldItems.map((i) => [i.category, i.quantity]));
  const newMap = new Map(newItems.map((i) => [i.category, i.quantity]));
  const categories = [...new Set([...oldMap.keys(), ...newMap.keys()])];
  return categories.map((category) => {
    const oldQty = oldMap.get(category) ?? 0;
    const newQty = newMap.get(category) ?? 0;
    return { category, oldQty, newQty, changed: oldQty !== newQty };
  });
}

/**
 * Duyệt/từ chối yêu cầu điều chỉnh SỐ LƯỢNG KIỂM ĐẾM.
 *
 * Lúc duyệt: nạp danh sách phiếu lỗi liên quan tới đơn (`related-fault-tickets`) để giám sát TỰ CHỌN
 * huỷ cái nào — KHÔNG tự động huỷ gì cả (đúng yêu cầu nghiệp vụ: số cũ có thể đã được trả một phần nợ,
 * huỷ mù theo số mới có thể sai).
 *
 * Sổ nợ đồ: đơn đã kiểm đếm giao thì BE tự ghi bút toán bù theo số mới khi duyệt — modal nạp bản XEM TRƯỚC
 * (`debt-impact`) để giám sát thấy sổ sẽ đổi gì; phần thiếu TĂNG thì bắt buộc chọn lý do.
 */
const CountingAdjustmentDetailModal: React.FC<Props> = ({ open, request, onClose, onUpdated }) => {
  const { fetch: fetchRelated, loading: loadingRelated } = AdjustmentRequestService.relatedFaultTickets();
  const { fetch: approve, loading: approving } = AdjustmentRequestService.approveCounting();
  const { fetch: reject, loading: rejecting } = AdjustmentRequestService.rejectCounting();
  const { fetch: fetchDebtImpact, loading: loadingDebtImpact } = AdjustmentRequestService.debtImpact();

  const [relatedTickets, setRelatedTickets] = useState<FaultTicket[]>([]);
  const [selectedTicketIds, setSelectedTicketIds] = useState<Set<number>>(new Set());
  const [reviewNote, setReviewNote] = useState("");
  const [debtImpact, setDebtImpact] = useState<CountingDebtImpact | null>(null);
  const [debtReason, setDebtReason] = useState<DebtShortageReason | "">("");

  useEffect(() => {
    setReviewNote("");
    setSelectedTicketIds(new Set());
    setRelatedTickets([]);
    setDebtImpact(null);
    setDebtReason("");
    if (request && request.status === ADJUSTMENT_REQUEST_STATUS.PENDING) {
      fetchRelated(request.id).then((res) => {
        if (Array.isArray(res?.data)) setRelatedTickets(res!.data as FaultTicket[]);
      });
      fetchDebtImpact(request.id).then((res) => {
        if (res?.data && typeof (res.data as any).applicable === "boolean") setDebtImpact(res.data as CountingDebtImpact);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.id]);

  if (!open || !request) return null;
  const isPending = request.status === ADJUSTMENT_REQUEST_STATUS.PENDING;

  const toggleTicket = (id: number) => {
    setSelectedTicketIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const needDebtReason = !!debtImpact?.reason_required;

  const handleApprove = async () => {
    if (needDebtReason && !debtReason) {
      showToast({ type: "error", message: "Chọn lý do cho phần thiếu tăng thêm để ghi sổ nợ đồ" });
      return;
    }
    try {
      const res = await approve(request.id, {
        review_note: reviewNote || undefined,
        cancel_fault_ticket_ids: selectedTicketIds.size ? [...selectedTicketIds] : undefined,
        debt_shortage_reason: needDebtReason && debtReason ? debtReason : undefined,
      });
      if (res && (res.status === 200 || res.status === undefined)) {
        const warnings: string[] = (res.data as any)?.debt_impact?.warnings ?? [];
        showToast({
          type: warnings.length ? "warning" : "success",
          message: warnings.length
            ? "Đã duyệt — còn phần nợ khách đã được trả bù, cần điều chỉnh sổ nợ đồ bằng tay"
            : "Đã duyệt yêu cầu điều chỉnh",
        });
        onUpdated();
        onClose();
      } else {
        showToast({ type: "error", message: res?.message || "Duyệt thất bại" });
      }
    } catch (e: any) {
      showToast({ type: "error", message: e?.message || "Duyệt thất bại" });
    }
  };

  const handleReject = async () => {
    if (!reviewNote.trim()) {
      showToast({ type: "error", message: "Vui lòng nhập lý do từ chối" });
      return;
    }
    try {
      const res = await reject(request.id, reviewNote.trim());
      if (res && (res.status === 200 || res.status === undefined)) {
        showToast({ type: "success", message: "Đã từ chối yêu cầu" });
        onUpdated();
        onClose();
      } else {
        showToast({ type: "error", message: res?.message || "Từ chối thất bại" });
      }
    } catch (e: any) {
      showToast({ type: "error", message: e?.message || "Từ chối thất bại" });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[560px] max-w-[95vw] p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800">Yêu cầu điều chỉnh #{request.id}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 text-lg">
            ✕
          </button>
        </div>

        <div className="divide-y mb-4">
          <Row label="Đơn" value={request.order_code || `#${request.order_id}`} />
          {request.customer_name && <Row label="Khách hàng" value={request.customer_name} />}
          <Row label="Mốc" value={STAGE_LABEL[request.stage]} />
          <Row label="Lý do" value={request.reason} />
          <Row label="Người tạo" value={request.requester_name || `NV #${request.requested_by}`} />
          <Row label="Ngày tạo" value={formatDateTime(request.created_at)} />
        </div>

        <div className="mb-4">
          <div className="text-sm font-semibold text-slate-700 mb-2">Số lượng đề xuất</div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            {/* `source_items` (số cũ) chỉ có khi BE đã enrich — undefined thì hiện danh sách trơn như cũ,
                tránh so 0 với số đề xuất rồi tô vàng nhầm cả bảng. */}
            {request.source_items ? (
              <>
                <div className="flex justify-between px-3 py-1.5 text-xs font-semibold text-slate-400 uppercase bg-slate-50 border-b border-slate-200">
                  <span>Loại đồ</span>
                  <span className="flex gap-6">
                    <span className="w-16 text-right">Số cũ</span>
                    <span className="w-20 text-right">Đề xuất</span>
                  </span>
                </div>
                {buildCompareRows(request.source_items, request.proposed_items).map((row, idx) => (
                  <div
                    key={row.category}
                    className={`flex justify-between items-center px-3 py-2 text-sm ${
                      row.changed ? "bg-amber-50" : idx % 2 ? "bg-slate-50" : "bg-white"
                    }`}
                  >
                    <span className={row.changed ? "text-amber-900 font-semibold" : "text-slate-700"}>
                      {row.category}
                    </span>
                    <span className="flex items-center gap-6">
                      <span className="w-16 text-right text-slate-400 line-through decoration-slate-300">
                        {row.changed ? `${row.oldQty} cái` : "—"}
                      </span>
                      <span className={`w-20 text-right font-semibold ${row.changed ? "text-amber-700" : "text-slate-800"}`}>
                        {row.newQty} cái
                      </span>
                    </span>
                  </div>
                ))}
              </>
            ) : (
              request.proposed_items.map((it, idx) => (
                <div
                  key={idx}
                  className={`flex justify-between px-3 py-2 text-sm ${idx % 2 ? "bg-slate-50" : "bg-white"}`}
                >
                  <span className="text-slate-700">{it.category}</span>
                  <span className="font-semibold text-slate-800">{it.quantity} cái</span>
                </div>
              ))
            )}
          </div>
        </div>

        {request.images && request.images.length > 0 && (
          <div className="mb-4">
            <div className="text-sm font-semibold text-slate-700 mb-2">Ảnh bằng chứng</div>
            <div className="flex flex-wrap gap-2">
              {request.images.map((img, idx) => (
                <ImageCustom
                  key={idx}
                  url={img}
                  isLocal={false}
                  isUser={false}
                  isReview={true}
                  className="w-20 h-20 object-cover rounded-lg border border-slate-200 cursor-pointer hover:opacity-90 transition"
                />
              ))}
            </div>
          </div>
        )}

        {isPending && (
          <div className="mb-4">
            <div className="text-sm font-semibold text-slate-700 mb-2">Sổ nợ đồ sau khi duyệt</div>
            {loadingDebtImpact ? (
              <div className="text-sm text-slate-400">Đang tính...</div>
            ) : !debtImpact ? (
              <div className="text-sm text-slate-400">Không tải được bản xem trước sổ nợ đồ.</div>
            ) : !debtImpact.applicable ? (
              <div className="text-sm text-slate-400">
                Đơn chưa kiểm đếm giao — sổ nợ đồ chưa phát sinh, không cần điều chỉnh.
              </div>
            ) : debtImpact.lines.length === 0 ? (
              <div className="text-sm text-slate-400">Sổ nợ đồ không thay đổi.</div>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden divide-y">
                {debtImpact.lines.map((line) => (
                  <div key={line.category} className="px-3 py-2 text-sm">
                    <div className="flex justify-between gap-3">
                      <span className="font-medium text-slate-800">{line.category}</span>
                      <span className="text-xs text-slate-500">
                        Đang ghi thiếu {line.recorded.owe + line.recorded.lost + line.recorded.non_standard} → theo số
                        mới {line.target}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {line.changes.map((c, i) => (
                        <span
                          key={i}
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            c.quantity < 0 ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"
                          }`}
                        >
                          {c.entry_type === 0
                            ? debtReason
                              ? DEBT_SHORTAGE_REASON_LABEL[debtReason]
                              : "Chờ chọn lý do"
                            : c.entry_label}{" "}
                          {c.quantity > 0 ? `+${c.quantity}` : c.quantity}
                        </span>
                      ))}
                      {line.unresolved > 0 && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700">
                          {line.unresolved} cái khách đã được trả bù — xử lý tay
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {debtImpact?.warnings?.map((w, i) => (
              <p key={i} className="mt-2 text-xs text-red-600">
                {w}
              </p>
            ))}
            {needDebtReason && (
              <div className="mt-3">
                <label className="block text-xs text-slate-600 mb-1">
                  Phần thiếu với khách TĂNG — ghi sổ theo lý do <span className="text-red-500">*</span>
                </label>
                <select
                  value={debtReason}
                  onChange={(e) => setDebtReason(e.target.value as DebtShortageReason | "")}
                  className={`w-full border rounded-lg px-3 py-2 text-sm ${debtReason ? "border-slate-200" : "border-red-400"}`}
                >
                  <option value="">— Chọn lý do —</option>
                  {(Object.keys(DEBT_SHORTAGE_REASON_LABEL) as DebtShortageReason[]).map((k) => (
                    <option key={k} value={k}>
                      {DEBT_SHORTAGE_REASON_LABEL[k]}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {isPending && (
          <div className="mb-4">
            <div className="text-sm font-semibold text-slate-700 mb-2">
              Phiếu lỗi liên quan tới đơn này — tick chọn để HUỶ cùng lúc (không tự động)
            </div>
            {loadingRelated ? (
              <div className="text-sm text-slate-400">Đang tải...</div>
            ) : relatedTickets.length === 0 ? (
              <div className="text-sm text-slate-400">Không có phiếu lỗi nào liên quan.</div>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden divide-y">
                {relatedTickets.map((t) => (
                  <label
                    key={t.id}
                    className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer ${
                      t.status === FAULT_STATUS.RESOLVED || t.status === FAULT_STATUS.REJECTED
                        ? "opacity-60"
                        : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTicketIds.has(t.id)}
                      onChange={() => toggleTicket(t.id)}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-slate-800">
                        {t.code} — {FAULT_TYPE_LABEL[t.fault_type] || t.fault_type}
                      </div>
                      <div className="text-xs text-slate-400">
                        {t.responsible_name || `NV #${t.responsible_user_id}`} ·{" "}
                        {FAULT_STATUS_LABEL[t.status] || t.status}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {isPending ? (
          <>
            <textarea
              className="w-full border border-slate-200 rounded-lg p-2 text-sm mb-4"
              rows={2}
              placeholder="Ghi chú của giám sát (không bắt buộc khi duyệt, BẮT BUỘC khi từ chối)"
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={handleReject}
                disabled={rejecting || approving}
                className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm font-semibold disabled:opacity-50"
              >
                Từ chối
              </button>
              <button
                onClick={handleApprove}
                disabled={rejecting || approving || loadingDebtImpact || (needDebtReason && !debtReason)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-semibold disabled:opacity-50"
              >
                {approving ? "Đang duyệt..." : "Duyệt"}
              </button>
            </div>
          </>
        ) : (
          <div className="divide-y">
            <Row label="Trạng thái" value={request.status === ADJUSTMENT_REQUEST_STATUS.APPROVED ? "Đã duyệt" : "Đã từ chối"} />
            <Row label="Người duyệt" value={request.reviewed_by ? request.reviewer_name || `NV #${request.reviewed_by}` : "—"} />
            {request.review_note && <Row label="Ghi chú" value={request.review_note} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default CountingAdjustmentDetailModal;
